// Paleta de la org → variables CSS del shell «Orion».
//
// La base (paleta del sitio de Orion, AA verificado) vive en index.css bajo
// `.orion-shell`. Si la org trae su paleta en datos (dashboard.theme o, en su
// defecto, el theme del sitio), aquí se traduce a los tokens shadcn (HSL) del
// modo activo. Cada token se valida: si un color no da contraste AA (4.5:1 para
// texto) se IGNORA y queda el valor base — nunca se pinta algo ilegible.

import type { ShellTheme, ShellThemeMode } from '@/context/DashboardShellContext';

type RGB = [number, number, number];

function parseColor(input: string | undefined, over?: RGB): RGB | null {
  if (!input || typeof input !== 'string') return null;
  const s = input.trim();
  const hex = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const rgba = s.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i);
  if (rgba) {
    const rgb: RGB = [Number(rgba[1]), Number(rgba[2]), Number(rgba[3])];
    const a = rgba[4] === undefined ? 1 : Math.max(0, Math.min(1, Number(rgba[4])));
    if (a >= 1 || !over) return rgb;
    // Mezcla sobre el fondo → color sólido equivalente.
    return [0, 1, 2].map((i) => Math.round(rgb[i] * a + over[i] * (1 - a))) as RGB;
  }
  return null;
}

function luminance([r, g, b]: RGB): number {
  const f = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

export function contrast(a: RGB, b: RGB): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

function toHslTriplet([r, g, b]: RGB): string {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const WHITE: RGB = [255, 255, 255];
const AA = 4.5;

/** Variables CSS a aplicar sobre `.orion-shell` para el modo dado. Vacío si la
 *  org no trae paleta (se usa la base de index.css). */
export function themeVars(theme: ShellTheme | null, mode: 'light' | 'dark'): Record<string, string> {
  const m: ShellThemeMode | undefined = theme?.[mode];
  if (!m) return {};
  const out: Record<string, string> = {};

  const card = parseColor(m.bg);
  const canvas = parseColor(m.bg_muted) || card;
  const fg = parseColor(m.fg);
  const fgMuted = parseColor(m.fg_muted);
  const acc = parseColor(m.acc1);
  const acc2 = parseColor(m.acc2);
  if (!card || !canvas) return {};

  // Texto principal: debe pasar AA sobre tarjeta Y lienzo, si no se ignora todo
  // (sin texto legible no tiene sentido cambiar las superficies).
  if (!fg || contrast(fg, card) < AA || contrast(fg, canvas) < AA) return {};

  out['--card'] = toHslTriplet(card);
  out['--popover'] = toHslTriplet(card);
  out['--background'] = toHslTriplet(canvas);
  out['--foreground'] = toHslTriplet(fg);
  out['--card-foreground'] = toHslTriplet(fg);
  out['--popover-foreground'] = toHslTriplet(fg);
  out['--secondary-foreground'] = toHslTriplet(fg);

  if (fgMuted && contrast(fgMuted, card) >= AA && contrast(fgMuted, canvas) >= AA) {
    out['--muted-foreground'] = toHslTriplet(fgMuted);
  }

  const border = parseColor(m.border, card);
  if (border) {
    out['--border'] = toHslTriplet(border);
    out['--input'] = toHslTriplet(border);
  }

  // Primario: se usa como texto/enlace sobre la tarjeta y como fondo de botón.
  if (acc && contrast(acc, card) >= AA) {
    const onAcc = contrast(WHITE, acc) >= contrast(canvas, acc) ? WHITE : canvas;
    if (contrast(onAcc, acc) >= AA) {
      out['--primary'] = toHslTriplet(acc);
      out['--ring'] = toHslTriplet(acc);
      out['--primary-foreground'] = toHslTriplet(onAcc);
      const hex = (c: RGB) => `#${c.map((x) => x.toString(16).padStart(2, '0')).join('')}`;
      out['--acc1'] = hex(acc);
      out['--accent'] = hex(acc);
      out['--acc1-rgb'] = acc.join(', ');
      out['--chart-bar'] = hex(acc);
      out['--chart-published'] = hex(acc);
    }
  }
  if (acc2 && contrast(acc2, card) >= 3) {
    out['--accent-indigo'] = toHslTriplet(acc2);
  }

  // Barra lateral (opcional): el texto claro de la barra (#EEEDFF) debe dar AA.
  const SIDE_FG: RGB = [238, 237, 255];
  const side = parseColor(m.sidebar_bg);
  if (side && contrast(SIDE_FG, side) >= AA) {
    out['--sidebar-bg'] = `rgb(${side.join(', ')})`;
    const sideAcc = parseColor(m.sidebar_accent);
    if (sideAcc && contrast(sideAcc, side) >= AA) {
      out['--sidebar-accent'] = `rgb(${sideAcc.join(', ')})`;
      out['--sidebar-active-accent'] = `rgba(${sideAcc.join(', ')}, .22)`;
      out['--sidebar-edge'] = `rgba(${sideAcc.join(', ')}, .22)`;
    }
  }
  // Degradado de botones principales («Nuevo chat», «Editar sitio»…): texto
  // blanco con AA en ambos extremos.
  //  1. Explícito (gradient_from/gradient_to de la org) → tal cual, como siempre.
  //  2. Si no viene, se DERIVA de acc1 (ver brandGradientStops).
  //  3. Si nada da AA → no se toca: queda el violeta de Orion de index.css.
  const g1 = parseColor(m.gradient_from);
  const g2 = parseColor(m.gradient_to);
  let stops: [RGB, RGB] | null = null;
  if (g1 && g2) {
    if (contrast(WHITE, g1) >= AA && contrast(WHITE, g2) >= AA) stops = [g1, g2];
  } else {
    stops = brandGradientStops(m.acc1, m.acc2);
    // Resplandor del botón en el color derivado (el explícito conserva el de siempre).
    if (stops) out['--brand-glow'] = `rgba(${stops[0].join(', ')}, .45)`;
  }
  if (stops) {
    const [a, b] = stops;
    out['--brand-gradient'] = `linear-gradient(135deg, rgb(${a.join(', ')}) 0%, rgb(${b.join(', ')}) 100%)`;
  }
  return out;
}

// ── Degradado derivado del color de la org ─────────────────────────────────

type HSL = [number, number, number]; // h 0-360, s 0-1, l 0-1

function rgbToHsl([r, g, b]: RGB): HSL {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
  }
  return [h, s, l];
}

function hslToRgb([h, s, l]: HSL): RGB {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const [r1, g1, b1] =
    hp < 1 ? [c, x, 0] : hp < 2 ? [x, c, 0] : hp < 3 ? [0, c, x] : hp < 4 ? [0, x, c] : hp < 5 ? [x, 0, c] : [c, 0, x];
  const mm = l - c / 2;
  return [r1, g1, b1].map((v) => Math.round(Math.max(0, Math.min(1, v + mm)) * 255)) as RGB;
}

/** Lo más oscuro que dejamos llegar un extremo: más abajo el color de la org ya
 *  no se reconoce (casi negro) → mejor el violeta de Orion. */
const MIN_LIGHTNESS = 0.14;

/** Oscurece (baja L en HSL, conserva tono y saturación) lo MÍNIMO para que el
 *  texto blanco dé AA. null si para lograrlo habría que bajar de MIN_LIGHTNESS. */
function darkenUntilAA(c: RGB): RGB | null {
  if (contrast(WHITE, c) >= AA) return c;
  const [h, s, l] = rgbToHsl(c);
  for (let li = l - 0.005; li >= MIN_LIGHTNESS; li -= 0.005) {
    const cand = hslToRgb([h, s, li]);
    if (contrast(WHITE, cand) >= AA) return cand;
  }
  return null;
}

/** Paleta propia de Orion (acc1 del sitio de Orion, claro/oscuro, y el violeta
 *  base): para esa paleta el degradado correcto ES el de index.css. */
const ORION_ACC1 = new Set(['#6d28d9', '#9575f7', '#7c3aed']);

/**
 * Extremos del degradado del botón principal derivados de acc1 (y acc2):
 *  · from = acc1 oscurecido lo necesario para que el texto blanco dé AA (≥4.5).
 *  · to   = un tono más oscuro que `from`, desplazado hacia acc2 si existe,
 *           también con AA.
 * null si falta acc1, si es la paleta de Orion o si no se logra AA en ambos
 * extremos sin perder el color (→ queda el violeta de Orion).
 */
export function brandGradientStops(acc1?: string, acc2?: string): [RGB, RGB] | null {
  if (acc1 && ORION_ACC1.has(acc1.trim().toLowerCase())) return null;
  const base = parseColor(acc1);
  if (!base) return null;
  const from = darkenUntilAA(base);
  if (!from) return null;

  const [fh, fs, fl] = rgbToHsl(from);
  let [h, sat] = [fh, fs];
  const second = parseColor(acc2);
  if (second) {
    const [h2, s2] = rgbToHsl(second);
    const dh = ((h2 - fh + 540) % 360) - 180;
    // Solo si acc2 es de la misma familia (≤90°): 35 % del camino hacia su tono
    // (máx. 25°). Con un acc2 lejano/complementario el degradado pasaría por un
    // tono ajeno (terracota→verde = café) → solo se oscurece.
    if (s2 > 0.12 && Math.abs(dh) <= 90) {
      h = (fh + Math.max(-25, Math.min(25, dh * 0.35)) + 360) % 360;
      sat = fs + (s2 - fs) * 0.35;
    }
  }
  // `to` debe verse MÁS oscuro que `from` (luminancia, no solo L de HSL: al
  // girar el tono la luminancia cambia) y dar AA.
  const target = Math.max(AA, contrast(WHITE, from) * 1.15);
  let to: RGB | null = null;
  for (let li = Math.max(MIN_LIGHTNESS, fl - 0.08); li >= MIN_LIGHTNESS - 1e-9; li -= 0.005) {
    const cand = hslToRgb([h, sat, li]);
    to = cand;
    if (contrast(WHITE, cand) >= target) break;
  }
  if (!to) return null;
  if (contrast(WHITE, from) < AA || contrast(WHITE, to) < AA) return null;
  return [from, to];
}
